import { useTranslation } from 'react-i18next';

const MissionVision = () => {
  const { t } = useTranslation();
  
  const values = [
    { icon: 'ri-heart-line', title: t('about.missionVision.items.community.title'), desc: t('about.missionVision.items.community.desc'), color: '#FF2DAA' },
    { icon: 'ri-lightbulb-line', title: t('about.missionVision.items.innovation.title'), desc: t('about.missionVision.items.innovation.desc'), color: '#8c52ff' },
    { icon: 'ri-shield-check-line', title: t('about.missionVision.items.integrity.title'), desc: t('about.missionVision.items.integrity.desc'), color: '#00c2ff' },
    { icon: 'ri-rocket-line', title: t('about.missionVision.items.results.title'), desc: t('about.missionVision.items.results.desc'), color: '#FF2DAA' },
  ];

  return (
    <section className="relative py-24 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(140,82,255,0.12)_0%,transparent_60%)]" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {[
            {
              icon: 'ri-focus-3-line',
              label: t('about.missionVision.mission.label'),
              color: '#8c52ff',
              title: t('about.missionVision.mission.title'),
              text: t('about.missionVision.mission.text'),
            },
            {
              icon: 'ri-eye-line',
              label: t('about.missionVision.vision.label'),
              color: '#00c2ff',
              title: t('about.missionVision.vision.title'),
              text: t('about.missionVision.vision.text'),
            },
          ].map((item) => (
            <div
              key={item.label}
              className="relative bg-white/3 border border-white/8 rounded-3xl p-8 overflow-hidden group hover:border-white/20 transition-all duration-500"
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(ellipse at 0% 0%, ${item.color}10 0%, transparent 60%)` }}
              />
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
                style={{ background: `${item.color}20`, border: `1px solid ${item.color}30` }}
              >
                <i className={`${item.icon} text-2xl`} style={{ color: item.color }} />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: item.color }}>
                {item.label}
              </span>
              <h3 className="text-white font-black text-xl md:text-2xl mb-4">{item.title}</h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="text-center mb-12">
          <p className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-3">{t('about.missionVision.valuesTitle')}</p>
          <h2 className="text-3xl md:text-4xl font-black text-white">
            {t('about.missionVision.valuesSub')} <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">{t('about.missionVision.valuesHighlight')}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v) => (
            <div
              key={v.title}
              className="group bg-white/3 border border-white/8 rounded-2xl p-6 text-center hover:border-white/20 transition-all duration-500 hover:-translate-y-1"
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${v.color}20`, border: `1px solid ${v.color}30` }}
              >
                <i className={`${v.icon} text-2xl`} style={{ color: v.color }} />
              </div>
              <h4 className="text-white font-bold text-base mb-2">{v.title}</h4>
              <p className="text-white/50 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* Experience Stats */}
        <div className="mt-20 bg-white/3 border border-white/8 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-10">
            <h3 className="text-white font-black text-2xl md:text-3xl">{t('about.missionVision.experienceTitle')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                area: t('about.missionVision.areas.marketing'),
                skills: ['Social Media Strategy', 'Content Marketing', 'SEO & SEM', 'Paid Advertising', 'Analytics'],
                color: '#8c52ff',
              },
              {
                area: t('about.missionVision.areas.tech'),
                skills: ['AI Automation Tools', 'Sales Funnels', 'Email Marketing', 'CRM Systems', 'Chatbots'],
                color: '#00c2ff',
              },
              {
                area: t('about.missionVision.areas.education'),
                skills: ['Online Courses', 'Group Coaching', '1:1 Mentoring', 'Workshops', 'Community Building'],
                color: '#FF2DAA',
              },
            ].map((area) => (
              <div key={area.area}>
                <h4 className="font-bold text-base mb-4" style={{ color: area.color }}>{area.area}</h4>
                <ul className="space-y-2">
                  {area.skills.map((skill) => (
                    <li key={skill} className="flex items-center gap-2 text-white/60 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: area.color }} />
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;
